// JewelleryTable.js
import React from "react";
import { Form, Table } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { renderValue } from "../../utils/jewelleryHelpers";

const VALIDATION_FIELD_MAP = {
  cut: "CutCode",
  shape: "ShapeCode",
  rm: "RawMaterialCode",
  mm_size: "MMSizeCode",
  commodity: "CommodityName",
  color: "ColorCode",
  sieve_size: "SieveSizeCode",
  setting_type: "SettingTypeName",
};

const EDITABLE_FIELDS = ["pp_wt", "pieces", "wt"];
const NUMERIC_FIELDS = ["pp_wt", "pieces", "wt", "min_wt", "max_wt"];

const JewelryTable = ({
  data,
  tableKey,
  validatedData = {},
  onSelectChange,
  selectedValues = {},
  hasValidated = false,
  isValidating = false,
}) => {
  if (!Array.isArray(data)) return null;

  // Get all unique columns from data array
  const columns = Array.from(
    data.reduce((acc, obj) => {
      Object.keys(obj).forEach(acc.add.bind(acc));
      return acc;
    }, new Set())
  );

  // Load options for AsyncSelect filtering by inputValue
  const loadOptions = async (inputValue, field) => {
    const options = validatedData[field] || [];
    const fieldKey = VALIDATION_FIELD_MAP[field.toLowerCase()] || "code";
    const query = (inputValue || "").toLowerCase().trim();

    return options
      .filter((item) =>
        String(item[fieldKey] || "").toLowerCase().includes(query)
      )
      .slice(0, 50)
      .map((item) => ({
        label: item[fieldKey],
        value: item[fieldKey],
        original: item,
      }));
  };

  // Determine border color based on validity
  const getBorderColor = (col, selectedValue) => {
  const lowerCol = col.toLowerCase();
  if (EDITABLE_FIELDS.includes(lowerCol)) return "#ced4da";

  const options = validatedData[lowerCol] || [];
  const fieldKey = VALIDATION_FIELD_MAP[lowerCol] || "code";

  const isValid = options.some(
    (opt) => String(opt[fieldKey]).toLowerCase() === String(selectedValue).toLowerCase()
  );

  return isValid ? "#28a745" : "#dc3545";
};

  return (
    <Table striped bordered hover responsive size="sm" className="mb-4">
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col}>{col.replace(/_/g, " ")}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {columns.map((col) => {
              const originalValue = row[col];
              const key = `${tableKey}_${rowIndex}_${col}`;
              const selectedValue = selectedValues[key] ?? originalValue;
              const lowerCol = col.toLowerCase();

              const isEditable = EDITABLE_FIELDS.includes(lowerCol);
              const isNumeric = NUMERIC_FIELDS.includes(lowerCol);
              const borderColor = getBorderColor(col, selectedValue);

              const options = validatedData[col] || [];
              const fieldKey = VALIDATION_FIELD_MAP[col] || "code";

              // Find matching option for selectedValue
              const matchedOption = options.find(
                (opt) =>
                  String(opt[fieldKey] || "").toLowerCase() ===
                  String(selectedValue).toLowerCase()
              );

              const selectValue = matchedOption
                ? { label: matchedOption[fieldKey], value: matchedOption[fieldKey] }
                : selectedValue
                  ? { label: selectedValue, value: selectedValue }
                  : null;

              return (
                <td key={col} style={{ minWidth: 120, verticalAlign: "top" }}>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      marginBottom: 4,
                      color: "#6c757d",
                      paddingBottom: 2,
                    }}
                  >
                    {renderValue(originalValue)}
                  </div>

                  {isEditable ? (
                    hasValidated && (
                      <Form.Control
                        size="sm"
                        type={isNumeric ? "number" : "text"}
                        value={selectedValue || ""}
                        step={lowerCol === "pp_wt" ? "0.001" : "1"}
                        onChange={(e) => {
                          const value = e.target.value;
                          const processedValue = isNumeric
                            ? lowerCol === "pp_wt"
                              ? parseFloat(value) || 0
                              : parseInt(value) || 0
                            : value;
                          onSelectChange(rowIndex, col, processedValue);
                        }}
                        style={{
                          fontSize: "0.75rem",
                          height: "30px",
                          padding: "0.25rem 0.5rem",
                          borderColor,
                        }}
                        placeholder={`Enter ${col}`}
                        disabled={isValidating}
                      />
                    )
                  ) : options.length > 0 ? (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={options.slice(0, 50).map((item) => ({
                        label: item[fieldKey],
                        value: item[fieldKey],
                        original: item,
                      }))}
                      loadOptions={(input) => loadOptions(input, col)}
                      onChange={(selected) =>
                        onSelectChange(rowIndex, col, selected?.value)
                      }
                      value={selectValue}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "30px",
                          borderColor,
                          boxShadow: "none",
                          "&:hover": { borderColor },
                        }),
                      }}
                      placeholder={`Select ${col}`}
                      isDisabled={isValidating}
                    />
                  ) : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default React.memo(JewelryTable);